import { useState } from 'react';
import { useSelector } from 'react-redux';
import { getImages, getReadyBy } from '../../../helpers/common';
import { EntityTypes } from '../../../new-lib/constants/common';
import EntityLayout from '../components/layout/EntityLayout';
import NawyImageGallery from '../../../new-components/NawyImageGallery';
import SuggestedContainer from '../../../new-components/SuggestedContainer';
import StickyDetails from '../components/entity-details/StickyDetails';
import EntityDetails from '../components/entity-details/EntityDetails';
import Description from '../components/Description';
import PaymentPlansContainer from '../components/payment-plans/PaymentPlansContainer';
import Backlinks from '../components/Backlinks';
import PropertyDetailsTable from '../components/PropertyDetailsTable';
import Amenities from '../components/Amenities';
import QuickView from '../../../new-components/display-system/QuickView';
import RequestMeetingBtn from '../../contact/components/request-meeting/RequestMeetingBtn';
import RequestMeetingContainer from '../../contact/components/request-meeting/RequestMeetingContainer';
import { logEvent } from '../../../new-lib/analytics';
import FormHeader from '../../contact/components/organic-form/FormHeader';
import FulfilledByTaskeen from '../components/FulfilledByTaskeen';
import CoverSection from '../launch/components/LaunchView/components/CoverSection';
import { useTranslation } from 'next-i18next';
import usePolygonType from '../../map/hooks/PolygonTypes';
import dynamic from 'next/dynamic';
import { RecommendedProperty } from '../../home/dtos';
import RecommendedNawyNowPropertyCard from '../../home/components/recommended-section/RecommendedNawyNowProperty';
import PropertyVirtualTourBanner from '../components/PropertyVirtualTourBanner';

const ContactFormContainer = dynamic(
  () => import('../../contact/components/organic-form/ContactFormContainer'),
  { ssr: false }
);

interface PropertyViewProps {
  property: any;
  availablePropertyTypes: any;
  filterOptions: any;
  breadcrumbs: React.ReactElement;
  financingProperty: RecommendedProperty | null;
  suggestedProperties?: any[];
  showOffers: boolean;
}

const PropertyView = ({
  property,
  availablePropertyTypes,
  breadcrumbs,
  financingProperty,
  suggestedProperties,
  showOffers
}: PropertyViewProps) => {
  const eventId = useSelector((state: any) => state.app.eventId);
  const { t } = useTranslation('common');
  const [meetingForm, setMeetingForm] = useState<boolean>(false);
  const images = getImages(property.images);
  const { getPolygonType } = usePolygonType();
  const handleRequestMeetingClick = () => {
    logEvent.contact('form_physical_meeting', `PropertyPage`, eventId, {
      recommended: property.recommended ?? false,
      recommended_type: property.financing ? 'Financing' : 'General',
      entity_type: EntityTypes.PROPERTY,
      prop_id: property.id,
      prop_name: property.name,
      org_id: property.nawy_organization_id
    });
    setMeetingForm(true);
  };
  if (images.length == 1) images.push(images[0]);

  const trackingPropertyData = {
    property_id: property.id,
    compound_id: property.compound.id,
    compound_name: property.compound.name,
    sale_type: property.sale_type,
    property_type: property.property_type.name
  };
  return (
    <EntityLayout>
      {breadcrumbs}

      {property.compound.nawy_organization_id === 2 && <FulfilledByTaskeen />}

      {property?.resale === false && property?.compound?.not_launched ? (
        <div className="new-launch-cover">
          <CoverSection
            marketingVideoPath={undefined}
            videoUrl={undefined}
            marketingImageSquarePath={property?.images[0]?.image_path}
            marketingImageWidePath={property?.images[0]?.image_path}
          />
          <div className="launch-logo">
            <span>{t('new')}</span>
            <span className="launch-text">{t('launch')}</span>
          </div>
        </div>
      ) : (
        images &&
        images.length > 0 && (
          <div className="gallery-container">
            <NawyImageGallery
              entity={property}
              type={EntityTypes.PROPERTY}
              images={images}
              fixedImage={images[images.length - 1]}
            />
          </div>
        )
      )}
      <div className="main-container">
        <StickyDetails
          entity={property}
          entityType={EntityTypes.PROPERTY}
          contactEventExtraParams={{
            entity_type: EntityTypes.PROPERTY,
            prop_id: property.id,
            prop_name: property.name,
            org_id: property.nawy_organization_id ?? 1,
            min_price: property?.min_price,
            max_price: property?.max_price,
            area_id: property?.compound?.area_id,
            area_name: property?.compound?.area?.name,
            min_unit_area: property?.min_unit_area,
            max_unit_area: property?.max_unit_area,
            no_beds: property?.number_of_bedrooms,
            no_baths: property?.number_of_bathrooms,
            dwn_pymnt_prcnt: property?.payment_plan?.down_payment?.percent,
            install_yrs: property?.payment_plan?.years,
            is_delivered: getReadyBy(property?.ready_by) == 'delivered'
          }}
        />
        <div className="entity-layout">
          <div className="details-side">
            <div className="propertyDetails">
              <PropertyDetailsTable dataPerRow={2} property={property} />
            </div>
            <EntityDetails
              entityType={EntityTypes.PROPERTY}
              galleryImages={property.images}
              floorPlanImages={property.floor_plans}
              masterPlanImage={property.compound.map_path}
              mapData={{
                lat:
                  property?.compound?.parent_compound?.location_point?.[1] ??
                  property?.compound.lat,
                long:
                  property?.compound?.parent_compound?.location_point?.[0] ??
                  property?.compound.long,
                id:
                  property?.compound?.parent_compound?.id ??
                  property?.compound?.id,
                image: property?.compound?.cover_image_path,
                isFiltered: true,
                name: property?.compound.name,
                geometry: {
                  type: getPolygonType(property?.compound?.polygon_points),
                  coordinates: property?.compound?.polygon_points
                },
                parent_id: property?.compound?.parent_compound?.id
              }}
              propertyData={trackingPropertyData}
              walkthroughVideo={property.walkthrough_video}
            />
            {property.amenities.length > 0 && (
              <Amenities amenities={property.amenities} />
            )}

            {property.walkthrough_video &&
              property.walkthrough_video_cover_image && (
                <PropertyVirtualTourBanner
                  walkthroughVideo={property.walkthrough_video}
                  walkthroughVideoCoverImage={
                    property.walkthrough_video_cover_image
                  }
                  propertyData={trackingPropertyData}
                />
              )}

            {(property?.payment_plans.length > 0 ||
              property?.offers.length > 0) && (
              <PaymentPlansContainer
                entityType={EntityTypes.PROPERTY}
                paymentPlans={property.payment_plans}
                showOffers={showOffers}
                offers={property.offers}
                entity={property}
                isFinancing={property.financing_new}
              />
            )}

            {property.description && (
              <Description
                headtxt={property.property_type.name}
                description={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: property.description
                    }}
                  />
                }
                directedUrlClickEvent={() =>
                  logEvent.click(
                    'cmpnd_click',
                    'AboutPropertySection',
                    'compound',
                    {
                      org_id: property.nawy_organization_id
                    }
                  )
                }
              />
            )}
          </div>

          <div className="banners-side">
            {financingProperty && (
              <RecommendedNawyNowPropertyCard
                primaryId={property.id}
                entity={financingProperty}
                isNawyNow={true}
                clickEventData={{
                  property: financingProperty,
                  eventAction: 'prop_click',
                  eventLocation: 'FinancingCardInPropertyPage',
                  recommended: true,
                  featureName: 'property'
                }}
                entityType={EntityTypes.PROPERTY}
                recommendedClick={true}
                cardPosition={0}
              />
            )}

            <div className="form-container">
              <div className="form">
                <ContactFormContainer
                  renderedInputs={{
                    name: true,
                    location: true,
                    message: true,
                    mobileNumber: true
                  }}
                  location={{ propertyId: property.id }}
                  forecastFlag={property.has_forecast}
                  layout={{ header: <FormHeader hasIcon={true} /> }}
                  organizationId={
                    property.nawy_organization_id === 2 &&
                    property.nawy_organization_id
                  }
                  compound={{
                    id: property?.compound?.id,
                    name: property?.compound?.name,
                    nawy_organization_id:
                      property?.compound?.nawy_organization_id,
                    area: {
                      id: property?.compound.area_id,
                      ...property?.compound.area
                    },
                    developer: {
                      id: property?.compound.developer?.id,
                      name: property?.compound.developer?.name
                    },
                    min_price: property?.min_price,
                    max_price: property?.max_price,
                    min_unit_area: property?.min_unit_area,
                    max_unit_area: property?.max_unit_area,
                    no_beds: property?.number_of_bedrooms,
                    no_baths: property?.number_of_bathrooms,
                    dwn_pymnt_prcnt:
                      property?.payment_plan?.down_payment?.percent,
                    install_yrs: property?.payment_plan?.years,
                    is_delivered: getReadyBy(property?.ready_by) == 'delivered'
                  }}
                />
              </div>
              <RequestMeetingBtn clickHandler={handleRequestMeetingClick} />
              {availablePropertyTypes.length > 0 && (
                <Backlinks
                  compoundDetails={{
                    name: property.compound.name,
                    slug: property.compound.slug
                  }}
                  availablePropertyTypes={availablePropertyTypes}
                />
              )}
            </div>
          </div>
        </div>
        {suggestedProperties && (
          <SuggestedContainer suggestedProperties={suggestedProperties} />
        )}
      </div>

      <QuickView isOpen={meetingForm} setIsOpen={setMeetingForm}>
        <RequestMeetingContainer
          location={{ propertyId: property.id }}
          setFormOpen={setMeetingForm}
        />
      </QuickView>
    </EntityLayout>
  );
};

export default PropertyView;
